import React, { useMemo, useState } from 'react';
import { Button, Chip, CircularProgress, Dialog, DialogContent, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { useQueryClient } from '@tanstack/react-query';

import { valuePropsApi } from 'api/catalog/valueProps';
import { ConfirmDialog } from 'shared/components/ConfirmDialog';
import {
    EXTERNAL_SKU_VALUE_PROPS_QUERY_KEY,
    useExternalSkuValuePropsQuery,
} from 'shared/hooks/catalog/useExternalSkuValuePropsQuery';
import { mapValuePropsToSelectOptions, useValuePropOptionsQuery } from 'shared/hooks/catalog/useValuePropOptionsQuery';

function ValuePropsCell({ row, setNotification }) {
    const queryClient = useQueryClient();
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [selectedToAdd, setSelectedToAdd] = useState([]);
    const [pendingRemove, setPendingRemove] = useState(null);

    const {
        data: allValueProps = [],
        error: optionsError,
        isError: isOptionsError,
        isLoading: isOptionsLoading,
    } = useValuePropOptionsQuery();

    const {
        data: assignedValueProps = [],
        error: assignedError,
        isError: isAssignedError,
        isLoading: isAssignedLoading,
    } = useExternalSkuValuePropsQuery({ externalSku: row.externalSku });

    const valuePropOptions = useMemo(() => mapValuePropsToSelectOptions(allValueProps), [allValueProps]);
    const assignedIds = useMemo(() => new Set(assignedValueProps.map((item) => item.id)), [assignedValueProps]);
    const availableOptions = valuePropOptions.filter((option) => !assignedIds.has(option.value));

    const invalidateAssigned = () =>
        queryClient.invalidateQueries({ queryKey: [EXTERNAL_SKU_VALUE_PROPS_QUERY_KEY, row.externalSku] });

    const handleAdd = async () => {
        const responses = await Promise.all(
            selectedToAdd.map((option) => valuePropsApi.addValuePropToExternalSku(row.externalSku, option.value)),
        );

        const failedOptions = [];
        let succeededCount = 0;

        responses.forEach((response, index) => {
            if (response.status) {
                succeededCount += 1;
            } else {
                failedOptions.push(selectedToAdd[index]);
            }
        });

        if (succeededCount > 0) {
            await invalidateAssigned();
        }

        if (failedOptions.length === 0) {
            setAddModalOpen(false);
            setSelectedToAdd([]);
            setNotification({
                text: succeededCount === 1 ? 'Value prop added' : `${succeededCount} value props added`,
                level: 'success',
            });
            return;
        }

        setSelectedToAdd(failedOptions);

        const firstFailure = responses.find((response) => !response.status);
        const failureMessage = firstFailure?.msg || 'Failed to add value prop';

        if (succeededCount > 0) {
            setNotification({
                text: `${succeededCount} added; ${failedOptions.length} failed (${failureMessage}). Retry the remaining value props.`,
                level: 'error',
            });
            return;
        }

        setNotification({
            text: failureMessage,
            level: 'error',
        });
    };

    const handleRemove = async (item) => {
        const response = await valuePropsApi.removeValuePropFromExternalSku(row.externalSku, item.id);

        if (response.status) {
            await invalidateAssigned();
            setNotification({ text: 'Value prop removed', level: 'success' });
            return;
        }

        setNotification({
            text: response.msg || 'Failed to remove value prop',
            level: 'error',
        });
    };

    const isLoading = isOptionsLoading || isAssignedLoading;

    return (
        <>
            {pendingRemove && (
                <ConfirmDialog
                    open
                    title="Remove value prop?"
                    description={`Remove "${pendingRemove.displayName}" from external SKU ${row.externalSku}?`}
                    confirmLabel="Remove"
                    confirmVariant="destructive"
                    onCancel={() => setPendingRemove(null)}
                    onConfirm={async () => {
                        await handleRemove(pendingRemove);
                        setPendingRemove(null);
                    }}
                />
            )}

            <Dialog open={addModalOpen} fullWidth maxWidth="sm" onClose={() => setAddModalOpen(false)}>
                <DialogContent>
                    <h4>Add value props for this item</h4>
                    {(isOptionsError || isAssignedError) && (
                        <p style={{ color: '#d32f2f', marginBottom: 16 }}>
                            {optionsError?.message || assignedError?.message || 'Failed to load value prop options'}
                        </p>
                    )}
                    {isLoading ? (
                        <CircularProgress size={24} />
                    ) : (
                        <Autocomplete
                            id="ValuePropsCell-Autocomplete"
                            multiple
                            options={availableOptions}
                            value={selectedToAdd}
                            onChange={(event, value) => setSelectedToAdd(value)}
                            getOptionLabel={(option) => option.text}
                            disabled={isOptionsError || isAssignedError}
                            style={{ width: 500 }}
                            renderInput={(params) => <TextField {...params} variant="outlined" label="Value props" />}
                        />
                    )}
                    <br />
                    <br />
                    <Button
                        id="ValuePropsCell-Add"
                        variant="outlined"
                        disabled={selectedToAdd.length === 0 || isLoading || isOptionsError || isAssignedError}
                        onClick={handleAdd}
                    >
                        Add Value Props
                    </Button>
                </DialogContent>
            </Dialog>

            <div style={{ textAlign: 'center' }}>
                {assignedValueProps.map((item) => (
                    <Chip
                        key={item.id}
                        size="small"
                        label={item.displayName}
                        color="primary"
                        style={{ margin: '2px 1px' }}
                        onDelete={() => setPendingRemove(item)}
                    />
                ))}
                <Chip
                    size="small"
                    clickable={!isLoading}
                    color="secondary"
                    style={{ margin: '2px' }}
                    label={isLoading ? '…' : '+'}
                    onClick={(event) => {
                        event.stopPropagation();
                        if (isLoading) {
                            return;
                        }
                        setSelectedToAdd([]);
                        setAddModalOpen(true);
                    }}
                />
            </div>
        </>
    );
}

/**
 * Stable DataTable cell renderer. Props are passed via column.valuePropCellProps
 * so react-table does not treat a new component type on every parent render.
 */
function ValuePropsTableCell({ column, row }) {
    const { setNotification } = column.valuePropCellProps ?? {};

    return <ValuePropsCell row={row.original} setNotification={setNotification} />;
}

export { ValuePropsCell, ValuePropsTableCell };
